import { ConnectedPosition, FlexibleConnectedPositionStrategy, GlobalPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { ElementRef, inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { take } from 'rxjs/operators';

export const OVERLAY_DATA = new InjectionToken<any>('OVERLAY_DATA');

export interface OverlayConfig<T> {
  data?: T;
  hasBackdrop?: boolean;
  disableBackdropClose?: boolean;
  positioning?: 'topLeft';
  overlayHeight?: number;
}

export interface DynamicOverlayResponse<C> {
  ref: OverlayRef;
  componentInstance: C;
}

export interface Bounds {
  top: number;
  left: number;
  bottom: number;
  height: number;
  right: number;
  width: number;
}

export interface Command {
  type: string;
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicOverlayService {
  private overlay = inject(Overlay);
  private injector = inject(Injector);
  private currentOverlayRef: OverlayRef | null = null;

  public open<C, D = any>(triggerElement: ElementRef | HTMLElement | Bounds, component: ComponentType<C>, config?: OverlayConfig<D>): DynamicOverlayResponse<C> {
    this.close();

    const positionStrategy = this.getPositionStrategy(triggerElement, config?.positioning, config?.overlayHeight);

    this.currentOverlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: config?.hasBackdrop ?? true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const customInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: OVERLAY_DATA, useValue: config?.data }],
    });

    const componentRef = this.currentOverlayRef.attach(new ComponentPortal(component, null, customInjector));

    if (!config?.disableBackdropClose) {
      if (config?.hasBackdrop === false) {
        this.currentOverlayRef
          .outsidePointerEvents()
          .pipe(take(1))
          .subscribe(() => this.close());
      } else {
        this.currentOverlayRef
          .backdropClick()
          .pipe(take(1))
          .subscribe(() => this.close());
      }
    }

    return {
      ref: this.currentOverlayRef,
      componentInstance: componentRef.instance,
    };
  }

  public close(): void {
    if (this.currentOverlayRef) {
      this.currentOverlayRef.dispose();
      this.currentOverlayRef = null;
    }
  }

  private getPositionStrategy(
    trigger: ElementRef | HTMLElement | Bounds,
    positioning?: 'topLeft',
    overlayHeight?: number
  ): FlexibleConnectedPositionStrategy | GlobalPositionStrategy {
    if (trigger instanceof ElementRef || trigger instanceof HTMLElement) {
      const element = trigger instanceof ElementRef ? trigger.nativeElement : trigger;

      if (positioning === 'topLeft') {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const leftOffset = rect.width < 100 ? rect.right + 10 : rect.left;

        if (overlayHeight && rect.top + overlayHeight > viewportHeight) {
          // Strategy when it overflows: position above the trigger.
          return this.overlay
            .position()
            .global()
            .bottom(`${viewportHeight - rect.top}px`)
            .left(`${leftOffset}px`);
        } else {
          // Default 'topLeft' strategy
          return this.overlay.position().global().top(`${rect.top}px`).left(`${leftOffset}px`);
        }
      }

      // Default strategy for buttons
      const positions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: -2 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: -2 },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
      ];

      return this.overlay.position().flexibleConnectedTo(element).withPositions(positions);
    } else {
      return this.overlay.position().global().top(`${trigger.top}px`).left(`${trigger.left}px`);
    }
  }
}
